"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { motion, useInView } from "framer-motion";
import { PageAffiliateV2Schema } from "@/sanity/pages/PageAffiliateV2Template";
import { CTAButton } from "@/shadcn/ui/button";
import { useRef } from "react";

// Duration (seconds) each line takes to grow
const LINE_DURATION = 0.8;
// Delay before the very first line starts (after the section comes into view)
const LINE_START_DELAY = 0.1;

export default function StepsSection({
  data,
  className,
}: {
  data: PageAffiliateV2Schema;
  className?: string;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const rightItemVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.2 + i * 0.1,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section
      className={cn(
        "relative overflow-clip pt-[3rem] lg:pt-[4.25rem] pb-[7rem] lg:pb-[10rem]",
        className,
      )}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full w-[0.125rem] bg-gradient-to-b from-[#E5E5E5] to-transparent",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="w-[min(157.125rem,100%)] mx-auto flex flex-col lg:items-center gap-[3.5rem] lg:gap-[5.5rem] px-[2rem] lg:px-0 relative">
        {/*bg-white border border-[#E5E5E5] px-[2rem] py-[4.5rem] lg:p-[9.38rem]*/}
        <div className="flex flex-col items-start lg:items-center">
          {data?.stepsCta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full lg:w-fit mb-[7rem] lg:mb-[15rem]"
            >
              <CTAButton data={data.stepsCta} className="" />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif text={data.stepsChip.text} />
          </motion.div>

          {data?.stepsText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext richtext-start"
            >
              <PortableText
                value={data.stepsText}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>
        <div
          ref={sectionRef}
          className="grid grid-cols-1 lg:grid-cols-3 gap-[2.25rem] relative"
        >
          {data.stepsItems &&
            data.stepsItems.length > 0 &&
            data.stepsItems.map((item, index) => (
              <motion.div
                key={"step" + index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={rightItemVariants}
                className="flex flex-col items-center gap-[3rem]"
              >
                <div className="lg:flex flex-col items-center relative w-full hidden">
                  <span className="text-[3rem] text-[#6DA991] mb-[3rem]">
                    0{index + 1}
                  </span>
                  <div className="rounded-full w-[1.5rem] h-[1.5rem] shrink-0 bg-[#6DA991]" />
                  {index !== 2 && (
                    <motion.div
                      className="absolute bottom-[0.55rem] left-1/2 w-[105%] h-[0.25rem] bg-[#6DA991] origin-left"
                      initial={{ scaleX: 0 }}
                      animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                      transition={{
                        duration: LINE_DURATION,
                        delay: LINE_START_DELAY + index * LINE_DURATION,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    />
                  )}
                </div>
                <div
                  className="flex-1 bg-[#FCFDFD] border border-[#00000005] p-[4rem] flex flex-col lg:items-center gap-[2rem] rounded-[1.375rem]"
                  style={{
                    boxShadow: "0 -1.257px 5.027px 0 rgba(0, 0, 0, 0.10) inset",
                  }}
                >
                  <Image src={item.icon} alt="" className="!w-[6rem]" />
                  <div
                    className="richtext richtext-start lg:richtext-center prose-p:mb-0 prose-strong:text-[#2E3436] prose-strong:font-medium prose-strong:text-[2.5rem] lg:prose-strong:text-[3rem] prose-p:[text-wrap-style:balance]
                    prose-p:text-[2rem] prose-p:text-[#1C1F22]/70"
                  >
                    <PortableText
                      value={item.text}
                      components={richTextComponent}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      </div>
    </section>
  );
}
